/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  type ProviderConfig as ProviderConfigBase,
  useFlags as useFlagsBase,
  useLDClient,
  withLDProvider,
} from 'launchdarkly-react-client-sdk';
import { useMemo, type ComponentType } from 'react';

import { mergeFlags, proxyFlags } from './base.js';

export { buildClientFeatureFlags as buildClientFeatureFlagsV2 } from './clientV2';

export type ProviderConfig = Omit<ProviderConfigBase, 'clientSideID'>;

type withClientFeatureFlagsFn = <T extends JSX.IntrinsicAttributes = object>(
  config?: ProviderConfig,
) => (WrappedComponent: ComponentType<T>) => ComponentType<T>;

export const buildClientFeatureFlags = <
  Flags extends { [key: string]: unknown },
  JSONFlagValues extends (keyof Flags)[] = (keyof Flags)[],
>({
  defaultFlagValues,
  enabled,
  jsonFlagValues,
}: {
  /**
   * A record of default values for flags. If no default is provided
   * for a flag, `false` will be the default.
   */
  defaultFlagValues?: Partial<Flags>;
  /**
   * Should usually be `typeof window !== 'undefined'`. This has to be passed
   * from the Next.js app and can't be computed in this library because of a
   * limitation of how Next is compiled.
   */
  enabled: boolean;
  /**
   * A list of flag names that should be automatically parsed as
   * an object from a JSON string.
   */
  jsonFlagValues?: JSONFlagValues;
}) => {
  // Setup dummy functions in the event that client-side flagging isn't enabled.
  let withClientFeatureFlags: withClientFeatureFlagsFn =
    (_config) => (WrappedComponent) => (props) => (
      <WrappedComponent {...props} />
    );
  let useFlags = () => proxyFlags<Flags>(defaultFlagValues);
  let useClient = () => ({}) as Partial<ReturnType<typeof useLDClient>>;

  if (enabled) {
    if (process.env.NEXT_PUBLIC_LAUNCH_DARKLY_ID) {
      // Init the LD provider with an env variable
      withClientFeatureFlags = (config) =>
        withLDProvider({
          ...config,
          clientSideID: process.env.NEXT_PUBLIC_LAUNCH_DARKLY_ID || '',
        });

      // Merge JSON parsing of flags and default values
      useFlags = () => {
        const flags = useFlagsBase<Flags>();
        return useMemo(
          () =>
            mergeFlags<Flags>(
              defaultFlagValues || {},
              flags,
              jsonFlagValues as string[],
            ),
          [flags],
        );
      };

      // Proxy the LD client
      useClient = useLDClient;
    } else {
      console.info(
        'Not initializing feature flags because NEXT_PUBLIC_LAUNCH_DARKLY_ID was not provided.',
      );
    }
  }

  return {
    /**
     * Get a record of all feature flags and their current values.
     * Recomputed when the flag values change.
     * Unlike LaunchDarkly's hook, this will decode JSON automatically.
     * https://launchdarkly.github.io/react-client-sdk/index.html#useFlags
     */
    useFlags,
    /**
     * Get the LaunchDarkly client.
     * https://launchdarkly.github.io/react-client-sdk/index.html#useLDClient
     */
    useClient,
    /**
     * Initialize a feature flag provider to wrap your app in.
     * Accepts all the parameters of [ProviderConfig](https://launchdarkly.github.io/react-client-sdk/index.html#ProviderConfig)
     * except `clientSideID`, which is automatically populated from
     * `process.env.NEXT_PUBLIC_LAUNCH_DARKLY_ID`.
     */
    withClientFeatureFlags,
  };
};
